import React, { useState, useEffect, useMemo } from "react";
import { File, Clock, CheckCircle, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { StatsCard } from "@/components/dashboard/StatsCard";
import { useLocation } from "wouter";
import { DateRangeFilter } from "@/components/common/DateRangeFilter";
import BASE_URL from "@/constants";
import { CardTilt } from "@/components/common/CardTilt";
import { motion } from 'framer-motion';
import axios from "axios";
import { ProgressiveLoader } from "@/components/ui/progressive-loader";
import FancySvgWithBoxes from "./loader";


type StageStatus = "pending" | "loading" | "success" | "error";
const PLATFORMS = [
  { value: "BlinkIt", logo: "/logos/blinkit.png" },
  { value: "Swiggy Instamart", logo: "/logos/swiggy.png" },
  { value: "Zepto", logo: "/logos/zepto.png" },
  { value: "BigBasket", logo: "/logos/bigbasket.png" },
  { value: "Amazon", logo: "/logos/amazon.png" },
  { value: "Flipkart", logo: "/logos/flipkart.png" },
  { value: "Grofers", logo: "/logos/grofers.png" },
  { value: "Dunzo", logo: "/logos/dunzo.png" },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

const Dashboard = () => {
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [client] = useState(localStorage.getItem("userClient") || "Novestar");
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [brandLevelData, setBrandLevelData] = useState<any[]>([]);
  const [platformBrandLevelData, setPlatformBrandLevelData] = useState<any[]>([]);
  const [showBrandInfo, setShowBrandInfo] = useState<boolean>(false);
  const [refresh, setRefresh] = useState(false);

      const initialStages = [
        { label: "Fetching POs from mail", status: "pending" as StageStatus },
        { label: "Syncing with latest data", status: "pending" as StageStatus },
        { label: "Aggregating dashboard", status: "pending" as StageStatus }
      ];
      
      // Create state for the stages
      const [stages, setStages] = useState(initialStages);
  
        const updateStage = (index: number, status: StageStatus) => {
        setStages(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], status };
          return updated;
        });
      };
  
  useEffect(() => {
    setLoading(true);
    setNoData(false);
    fetch(`${BASE_URL}/po-dashboard/fetch?companyName=${client}`)
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to fetch dashboard data");
        const data = await res.json();
        setDashboardData(data);
        setNoData(!data || !data.platformWiseData || data.platformWiseData.length === 0);
      })
      .catch(() => setNoData(true))
      .finally(() => setLoading(false));
  }, [client]);

  const handleRefresh = async () => {
    try {
      setRefresh(true);
      
  
      
  
  
      // Stage 1: Trigger Microservices
      updateStage(0, "loading");
      try {
        await Promise.all([
          axios.post("https://po-microservice-93503779962.asia-south2.run.app/scrape-blinkit-po", {
            platform: 0,
          }),
          axios.post("https://po-microservice-93503779962.asia-south2.run.app/scrape-zepto-po", {
            platform: 0,
          }),
        ]);
        updateStage(0, "success");
      } catch (error) {
        console.error("Error in stage 1:", error);
        updateStage(0, "error");
        throw error; // Re-throw to stop further execution
      }
  
      // Stage 2: Sync APIs
      updateStage(1, "loading");
      try {
        await Promise.all([
          axios.post(`${BASE_URL}/api/po-excel/sync-swiggy`),
          axios.post(`${BASE_URL}/api/po-excel/sync-blinkit`),
          axios.post(`${BASE_URL}/api/po-excel/sync-Zepto`),
        ]);
        updateStage(1, "success");
      } catch (error) {
        console.error("Error in stage 2:", error);
        updateStage(1, "error");
        throw error;
      }
  
      // Stage 3: Aggregate APIs
      updateStage(2, "loading");
      try {
        await Promise.all([
          axios.get(`${BASE_URL}/po-dashboard/brand-level`, {
            params: { client: client },
          }),
          axios.post(`${BASE_URL}/po-dashboard/aggregate`, {
            companyName: client,
          }),
        ]);
        updateStage(2, "success");
      } catch (error) {
        console.error("Error in stage 3:", error);
        updateStage(2, "error");
        throw error;
      }
  
   toast({ title: "Refresh Complete" });
 
       
    } catch (error) {
        toast({ title: "Error during refresh", description: error, variant: "destructive" });
       
  
      // alert("Something went wrong. Please check the console.");
    } finally {
      setStages(initialStages)
      setRefresh(false);
    }
  };
  const fetchDashboardByDateRange = async (dateFrom: Date, dateTo: Date) => {
    setLoading(true);
    setNoData(false);
    try {
      const statsPromise = fetch(`${BASE_URL}/po-dashboard/stats-in-range`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: client,
          dateFrom: dateFrom.toISOString(),
          dateTo: dateTo.toISOString(),
        }),
      });
      const brandPromise = fetch(`${BASE_URL}/po-dashboard/brand-level-range`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client: client,
          dateFrom: dateFrom.toISOString(),
          dateTo: dateTo.toISOString(),
        }),
      });

      const [statsRes, brandRes] = await Promise.all([statsPromise, brandPromise]);

      if (!statsRes.ok) throw new Error("Failed to fetch dashboard data by date range");
      if (!brandRes.ok) throw new Error("Failed to fetch brand-level data by date range");

      const statsData = await statsRes.json();
      const brandData = await brandRes.json();

      setDashboardData(statsData);
      const mapped = brandData.map((item: any) => {
        const getTeam = (teamName: string) => item.teams.find((t: any) => t.team.toLowerCase() === teamName.toLowerCase()) || {};
        return {
          brandName: item.brand,
          planner: { pending: getTeam("Planner").pending ?? "-", value: getTeam("Planner").totalValue ?? null },
          appointment: { pending: getTeam("Appointment").pending ?? "-", value: getTeam("Appointment").totalValue ?? null },
          salesOrder: { pending: getTeam("SalesOrder").pending ?? "-", value: getTeam("SalesOrder").totalValue ?? null },
          courier: { pending: getTeam("Courier").pending ?? "-", value: getTeam("Courier").totalValue ?? null },
          invoice: { pending: getTeam("Invoice").pending ?? "-", value: getTeam("Invoice").totalValue ?? null },
          warehouse: { pending: getTeam("Warehouse").pending ?? "-", value: getTeam("Warehouse").totalValue ?? null },
        };
      });
      setBrandLevelData(mapped);

      setNoData(!statsData || !statsData.platformWiseData || statsData.platformWiseData.length === 0);
    } catch (e) {
      setNoData(true);
      setBrandLevelData([]);
    } finally {
      setLoading(false);
    }
  };

  const platformLevelData = useMemo(() => {
    if (!dashboardData || !dashboardData.platformWiseData) return [];
    return dashboardData.platformWiseData.map((platform: any) => {
      const found = PLATFORMS.find((p) => p.value === platform.platformName);
      return {
        platform: platform.platformName,
        logo: found ? found.logo : "",
        inProgress: platform.inProgress,
        expiredPOs: platform.expiredPOs,
        totalPOs: platform.totalPOs,
        totalValue: platform.totalValue,
        valueChange: "0%", // Placeholder
      };
    });
  }, [dashboardData]);

  const totalPOs = dashboardData?.totalPOs || 0;
  const inProgressPOs = dashboardData?.inProgress || 0;
  const completedPOs = dashboardData?.completed || 0;

  useEffect(() => {
    const fetchBrandLevelData = async () => {
      try {
        const res = await fetch(`${BASE_URL}/po-dashboard/brand-level-data?client=${client}`);
        if (!res.ok) throw new Error("Failed to fetch brand-level PO data");
        const data = await res.json();
        const mapped = data.map((item: any) => {
          const getTeam = (teamName: string) => item.teams.find((t: any) => t.team.toLowerCase() === teamName.toLowerCase()) || {};
          return {
            brandName: item.brand,
            planner: { pending: getTeam("Planner").pending ?? "-", value: getTeam("Planner").totalValue ?? null },
            appointment: { pending: getTeam("Appointment").pending ?? "-", value: getTeam("Appointment").totalValue ?? null },
            salesOrder: { pending: getTeam("SalesOrder").pending ?? "-", value: getTeam("SalesOrder").totalValue ?? null },
            courier: { pending: getTeam("Courier").pending ?? "-", value: getTeam("Courier").totalValue ?? null },
            invoice: { pending: getTeam("Invoice").pending ?? "-", value: getTeam("Invoice").totalValue ?? null },
            warehouse: { pending: getTeam("Warehouse").pending ?? "-", value: getTeam("Warehouse").totalValue ?? null },
          };
        });
        setBrandLevelData(mapped);
        setPlatformBrandLevelData(mapped);
      } catch (e) {
        setBrandLevelData([]);
      }
    };
    fetchBrandLevelData();
  }, [client]);

  const handleDateRangeChange = (dateRange: { from: Date; to: Date } | null) => {
    if (!dateRange) {
      setLoading(true);
      setNoData(false);
      fetch(`${BASE_URL}/po-dashboard/fetch?companyName=${client}`)
        .then(async (res) => {
          if (!res.ok) throw new Error("Failed to fetch dashboard data");
          const data = await res.json();
          setDashboardData(data);
          setNoData(!data || !data.platformWiseData || data.platformWiseData.length === 0);
        })
        .catch(() => setNoData(true))
        .finally(() => setLoading(false));
      return;
    }
    if (dateRange.from && dateRange.to) {
      fetchDashboardByDateRange(dateRange.from, dateRange.to);
    }
  };

  return (
    <main className="p-4 sm:px-6 lg:px-8 relative">
            {
              refresh &&     <ProgressiveLoader stages={stages} />
            }
          

      {/* Page header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Overview of your purchase order management system</p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2">
            <motion.button
            whileHover={{ scale: 1.08,  }}
            whileTap={{ scale: 0.98 }}
            onClick={handleRefresh}
            // onClick={() => toast({ title: "Under Development", description: "This feature is under development." })}
            className="flex items-center gap-2 border border-gray-300 rounded px-4 py-2 bg-white"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </motion.button>
          <DateRangeFilter onDateRangeChange={handleDateRangeChange} />
        </div>
      </div>
{/* //loading */}
      {loading ? (
        // <div className="text-center text-gray-500 py-10">Loading...</div>
        <FancySvgWithBoxes />
      ) : noData ? (
        <div className="text-center text-red-500 py-10">No Data received for these Platforms</div>
      ) : !dashboardData ? null : (
        <>
          {/* Stats Overview */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 mb-6">
            <CardTilt>
              <StatsCard title="Total POs" value={totalPOs} icon={<File className="h-5 w-5 text-primary" />} />
            </CardTilt>
            <CardTilt>
              <StatsCard title="Expired POs" value={dashboardData?.expiredPOs || 0} icon={<Clock className="h-5 w-5 text-red-500" />} />
            </CardTilt>
            <CardTilt>
              <StatsCard title="In Progress" value={inProgressPOs} icon={<Clock className="h-5 w-5 text-warning" />} />
            </CardTilt>
            <CardTilt>
              <StatsCard title="Completed PO Tasks" value={completedPOs} icon={<CheckCircle className="h-5 w-5 text-success" />} />
            </CardTilt>
          </div>

          {/* Platform Level PO Cards */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Platform Level PO</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
              {platformLevelData.map((platform) => (
                <CardTilt key={platform.platform}>
                  <div className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-lg hover:border-blue-400 transition-all cursor-pointer">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex flex-col gap-1">
                        <img src={platform.logo} alt={platform.platform} className="h-6 w-24 object-contain" />
                        <span className="text-xs font-medium text-green-600">{platform.valueChange}</span>
                      </div>
                    </div>
                    {/* Stats Section */}
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">In Progress</span>
                        <span className="text-lg font-semibold text-green-600">{platform.inProgress}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Expired POs</span>
                        <span className="text-lg font-semibold text-red-500">{platform.expiredPOs}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">Total POs</span>
                        <span className="text-lg font-semibold text-gray-800">{platform.totalPOs}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-dashed border-gray-200">
                        <span className="text-gray-500">Total Value</span>
                        <span className="text-base font-bold text-gray-900">{formatCurrency(platform.totalValue)}</span>
                      </div>
                    </div>
                  </div>
                </CardTilt>
              ))}
            </div>
          </div>
          {/* Brand Level PO Table */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Brand Level PO</h2>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-gray-50" rowSpan={2}>
                        Brand Name
                      </th>
                      <th colSpan={2} className="text-center text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-green-50">
                        Planner
                      </th>
                      <th colSpan={2} className="text-center text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-purple-50">
                        Appointment
                      </th>
                      <th colSpan={2} className="text-center text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-yellow-50">
                        Sales Order
                      </th>
                      <th colSpan={2} className="text-center text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-orange-50">
                        Courier
                      </th>
                      <th colSpan={2} className="text-center text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-blue-50">
                        Invoice
                      </th>
                      <th colSpan={2} className="text-center text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-teal-50">
                        Warehouse
                      </th>
                    </tr>
                    <tr className="bg-gray-50">
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-green-50">Pending PO</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-green-50">Value</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-purple-50">Pending PO</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-purple-50">Value</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-yellow-50">Pending PO</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-yellow-50">Value</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-orange-50">Pending PO</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-orange-50">Value</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-blue-50">Pending PO</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-blue-50">Value</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-teal-50">Pending PO</th>
                      <th className="text-center p-1 text-xs font-bold text-gray-700 uppercase tracking-wider bg-teal-50">Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {brandLevelData.length === 0 ? (
                      <tr>
                        <td colSpan={15} className="text-center text-gray-400 py-6">
                          No brandwise data available
                        </td>
                      </tr>
                    ) : (
                      brandLevelData.map((brand, index) => (
                        <tr
                          key={brand.brandName}
                          className={`cursor-pointer ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                        >
                          <td
                            // onClick={() => setSelectedBrand(brand.brandName)}
                            className={`px-4 ${
                              brand?.brandName === selectedBrand ? "text-blue-500" : "text-black"
                            } py-3 whitespace-nowrap text-sm font-medium border-r bg-inherit`}
                          >
                            {brand.brandName}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-green-25">
                            {brand.planner?.pending ?? "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-green-25">
                            {brand.planner?.value ? formatCurrency(brand.planner.value) : "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-purple-25">
                            {brand.appointment?.pending ?? "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-purple-25">
                            {brand.appointment?.value ? formatCurrency(brand.appointment.value) : "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-yellow-25">
                            {brand.salesOrder?.pending ?? "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-yellow-25">
                            {brand.salesOrder?.value ? formatCurrency(brand.salesOrder.value) : "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-orange-25">
                            {brand.courier?.pending ?? "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-orange-25">
                            {brand.courier?.value ? formatCurrency(brand.courier.value) : "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-blue-25">
                            {brand.invoice?.pending ?? "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-blue-25">
                            {brand.invoice?.value ? formatCurrency(brand.invoice.value) : "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-teal-25">
                            {brand.warehouse?.pending ?? "-"}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center bg-teal-25">
                            {brand.warehouse?.value ? formatCurrency(brand.warehouse.value) : "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* Brand Table for Selected Brand */}
          {selectedBrand && (
            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <h2 className="text-xl font-semibold text-gray-900 ">
                  Brand Level PO <span className="text-gray-600 ">({selectedBrand})</span>
                </h2>
                <button
                  onClick={() => setSelectedBrand(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  
                </button>
              </div>

            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                 <thead>
  <tr className="bg-gray-50">
    <th
      className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r bg-gray-50"
      rowSpan={2}
    >
      Brand Name
    </th>

    <th colSpan={2} className="text-center  text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-green-50">
      Planner
    </th>
    <th colSpan={2} className="text-center  text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-purple-50">
      Appointment
    </th>
    <th colSpan={2} className="text-center  text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-yellow-50">
      Sales Order
    </th>
    <th colSpan={2} className="text-center  text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-orange-50">
      Courier
    </th>
    <th colSpan={2} className="text-center  text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-blue-50">
      Invoice
    </th>
    <th colSpan={2} className="text-center  text-xs p-2 border-bottom font-bold text-gray-700 uppercase tracking-wider border-b bg-teal-50">
      Warehouse
    </th>
  </tr>

  <tr className="bg-gray-50">
    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-green-50">Pending PO</th>
    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-green-50">Value</th>

    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-purple-50">Pending PO</th>
    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-purple-50">Value</th>

    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-yellow-50">Pending PO</th>
    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-yellow-50">Value</th>

    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-orange-50">Pending PO</th>
    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-orange-50">Value</th>

    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-blue-50">Pending PO</th>
    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-blue-50">Value</th>

    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider border-r bg-teal-50">Pending PO</th>
    <th className="text-center text-xs p-1 font-bold text-gray-700 uppercase tracking-wider bg-teal-50">Value</th>
  </tr>
</thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {brandLevelData.length === 0 ? (
                      <tr>
                        <td colSpan={15} className="text-center text-gray-400 py-6">No brandwise data available</td>
                      </tr>
                    ) : (
                      brandLevelData.map((brand, index) => (
                        <tr key={brand.brandName} className={`cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}> 
                          <td onClick={()=>setShowBrandInfo(true)} className="px-4 py-3 whitespace-nowrap text-sm font-medium border-r bg-inherit">
                            {brand.brandName}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-green-25">
                            {brand.planner?.pending ?? '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-green-25">
                            {brand.planner?.value ? formatCurrency(brand.planner.value) : '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-purple-25">
                            {brand.appointment?.pending ?? '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-purple-25">
                            {brand.appointment?.value ? formatCurrency(brand.appointment.value) : '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-yellow-25">
                            {brand.salesOrder?.pending ?? '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-yellow-25">
                            {brand.salesOrder?.value ? formatCurrency(brand.salesOrder.value) : '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-orange-25">
                            {brand.courier?.pending ?? '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-orange-25">
                            {brand.courier?.value ? formatCurrency(brand.courier.value) : '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-blue-25">
                            {brand.invoice?.pending ?? '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-blue-25">
                            {brand.invoice?.value ? formatCurrency(brand.invoice.value) : '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center border-r bg-teal-25">
                            {brand.warehouse?.pending ?? '-'}
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap text-sm text-gray-900 text-center bg-teal-25">
                            {brand.warehouse?.value ? formatCurrency(brand.warehouse.value) : '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              </div>

    
            </div>
          )}
        </>
      )}
    </main>
  );
};

export default Dashboard;
